import { AlertCircle, AlertTriangle } from 'lucide-react';
import { ACCENT, OK, WARN, VIOLET, BAD } from '../../lib/uiTokens';

/**
 * Sdílené názvosloví Banky — katalogy, popisky a formátování.
 *
 * Bydlí to mimo komponenty, protože totéž čte seznam, detail pohybu, panel
 * statistik i všech pět modalů. Do 3.481 to bylo v `FinanceBanka.jsx` vedle
 * rozvržení, takže se to nedalo použít nikde jinde, aniž se importoval celý
 * modul.
 */

export const fmt = (v) => (v == null ? '—' : new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(v));

/** Bez haléřů — pro souhrnná čísla v panelu, kde na nich nezáleží. */
export const fmtKratce = (v) => (v == null ? '—' : new Intl.NumberFormat('cs-CZ', {
    maximumFractionDigits: 0,
}).format(v));

export const fmtDate = (d) => (d ? new Intl.DateTimeFormat('cs-CZ').format(new Date(d)) : '—');

/** Krátké datum do řádku seznamu — „12. 9.“ */
export const fmtDenMesic = (d) => {
    if (!d) return '—';
    const x = new Date(d);
    return `${x.getDate()}. ${x.getMonth() + 1}.`;
};

// Čitelný název protistrany. U kartovních transakcí posílá GPC jen maskované
// číslo karty (XXXXXXXXXXXX4356 d.t) místo obchodníka → zobraz „Platba kartou
// •••4356". Syrová data (protistrana_nazev) necháváme být kvůli párování dle karty;
// merchanta se dozvíš z připojeného dokladu.
export const maskaKarty = (s) => (s || '').match(/[X*]{4,}\s*(\d{4})\b/i);

export const zobrazProtistranu = (t) => {
    const m = maskaKarty(t?.protistrana_nazev);
    if (m) return `Platba kartou •••${m[1]}`;
    return t?.protistrana_nazev || t?.popis || '—';
};

export const STAV_LABEL = {
    neparovano: 'Nespárováno',
    navrh: 'Návrh',
    paroveno: 'Spárováno',
    zarazeno: 'Zařazeno',
    ignorovano: 'Nepárovatelné',
};

export const STAV_BARVA = {
    neparovano: WARN,
    navrh: ACCENT,
    paroveno: OK,
    zarazeno: VIOLET,
    ignorovano: 'var(--text-tertiary)',
};

// Kategorie pro platby BEZ dokladu (odvody, daně, splátky…) — hodnota `tag` → popisek
export const KATEGORIE = [
    { tag: 'odvody_socialni', label: 'Odvody – sociální (OSSZ)' },
    { tag: 'odvody_zdravotni', label: 'Odvody – zdravotní (VZP/ZP)' },
    { tag: 'odvody_osvc', label: 'Odvody OSVČ (SP+ZP)' },
    { tag: 'dph', label: 'DPH (finanční úřad)' },
    { tag: 'dan_prijem', label: 'Daň z příjmu / zálohy' },
    { tag: 'mzdy', label: 'Mzdy / výplaty' },
    { tag: 'zalohy_energie', label: 'Zálohy na energie' },
    { tag: 'uver', label: 'Splátka úvěru / hypotéka' },
    { tag: 'prevod', label: 'Převod mezi účty' },
    { tag: 'vyber_bankomat', label: 'Výběr z bankomatu' },
    { tag: 'poplatky', label: 'Bankovní poplatky' },
    { tag: 'trzba', label: 'Příchozí – tržba' },
    { tag: 'osobni', label: 'Osobní (nepočítat)' },
    { tag: 'ostatni', label: 'Ostatní (bez dokladu)' },
];

export const KAT_LABEL = Object.fromEntries(KATEGORIE.map(k => [k.tag, k.label]));

/**
 * Pohledy jako DATA, ne devět pilulek v řadě (norma „Kolik pohledů unese
 * pilulková řada"). `stav` filtruje podle stavu párování, `diagnostika` je
 * nález, jehož pravidlo drží backend — pilulka a seznam tak nikdy neukážou
 * jiné číslo. Zkratku nad seznamem dostane jen pohled s `upozorneni` a vlastní
 * `barva`, tedy ten, který sám hlásí, že něco není v pořádku.
 *
 * Směr (příchozí/odchozí) a částka tu schválně nejsou — to je osa, kterou
 * kombinuješ s pohledem, ne pohled sám. Patří do filtru.
 */
export const POHLEDY = [
    { id: 'vse', label: 'Vše', skupina: 'Fronty', stav: null },
    { id: 'neparovano', label: 'Nespárované', skupina: 'Fronty', stav: 'neparovano' },
    { id: 'navrh', label: 'Návrh k potvrzení', skupina: 'Fronty', stav: 'navrh' },
    { id: 'paroveno', label: 'Spárované', skupina: 'Fronty', stav: 'paroveno' },
    { id: 'zarazeno', label: 'Zařazené bez dokladu', skupina: 'Fronty', stav: 'zarazeno' },
    { id: 'stare', label: 'Nespárované starší 14 dní', skupina: 'Co je špatně', diagnostika: 'stare_nesparovane', upozorneni: true, barva: BAD, Icon: AlertCircle },
    { id: 'bez_zarazeni', label: 'Příchozí bez zařazení', skupina: 'Co je špatně', diagnostika: 'prichozi_bez_zarazeni', upozorneni: true, barva: WARN, Icon: AlertTriangle },
    { id: 'ignorovano', label: 'Nepárovatelné', skupina: 'Archiv', stav: 'ignorovano' },
];

/** Počet u pohledu — ze statistik, ať sedí s tím, co seznam ukáže. */
export function pocetPohledu(p, staty) {
    if (!staty) return null;
    if (p.diagnostika === 'stare_nesparovane') return staty.stare_nesparovane ?? null;
    if (p.diagnostika === 'prichozi_bez_zarazeni') return staty.prichozi_bez_zarazeni ?? null;
    if (p.stav === null) return staty.pocet ?? null;
    return staty.podle_stavu?.[p.stav] ?? 0;
}

/** Pásma částky ve filtru — dvě číselná pole nahradila tři čipy. */
export const CASTKA_PASMA = {
    do1000: { min: null, max: 1000 },
    stred: { min: 1000, max: 10000 },
    nad10000: { min: 10000, max: null },
};

export const FILTR_SKUPINY = [
    {
        id: 'smer',
        label: 'Směr',
        labelKratky: 'Směr',
        typ: 'volby',
        volby: [
            { value: 'prichozi', label: 'Příchozí' },
            { value: 'odchozi', label: 'Odchozí' },
        ],
    },
    {
        id: 'castka',
        label: 'Částka',
        labelKratky: 'Částka',
        typ: 'volby',
        volby: [
            { value: 'do1000', label: 'do 1 000' },
            { value: 'stred', label: '1 – 10 tis.' },
            { value: 'nad10000', label: 'nad 10 tis.' },
        ],
    },
    {
        id: 'tag',
        label: 'Zařazení',
        labelKratky: 'Zařazení',
        typ: 'dropdown',
        vychozi: '',
        placeholder: 'Zařazení: vše',
        options: [{ value: '', label: 'Vše' }, ...KATEGORIE.map(k => ({ value: k.tag, label: k.label }))],
    },
];

export const token = () => document.querySelector('meta[name="csrf-token"]')?.content;

export const postJson = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token() },
    body: JSON.stringify(body || {}),
});
